/**
 * Service layer — Construction · Submittals (Supabase-backed)
 *
 * Wraps /api/submittals. Second CA sub-module; scoped to a construction_contract and
 * linked 1:1 to a Cortex Thread (ownership/follow-up lives on the thread).
 *
 * Round 11: extended with revision chain (submittal_revisions), spec-section-scoped
 * sequential numbering, and transmittal PDF write-back.
 */

async function call(method, body, qs) {
  const token = await window.__clerkGetToken()
  const url = '/api/submittals' + (qs ? '?' + qs : '')
  const res = await fetch(url, {
    method,
    headers: method === 'GET'
      ? { 'Authorization': 'Bearer ' + token }
      : { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: method === 'GET' ? undefined : JSON.stringify(body),
  })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b.error || 'Submittal request failed: ' + res.status)
  }
  return res.json()
}

export const SUBMITTAL_STATUS_LABELS = { open: 'Open', closed: 'Closed' }
export const SUBMITTAL_TYPES = ['Product Data', 'Sample', 'Shop Drawings', 'Other']
export const SUBMITTAL_REVIEWING_PARTIES = [
  'Architect', 'Civil Engineer', 'Landscape Architect', 'Structural Engineer',
  'Mechanical Engineer', 'Electrical Engineer', 'Interior Designer', 'Lighting Designer',
  'Kitchen Designer', 'Other',
]
export const SUBMITTAL_DISPOSITIONS = [
  'No Exceptions Taken', 'Revise as Noted', 'Revise and Resubmit',
  'Submit Requested Items Only', 'Not Accepted',
]

/**
 * Multi-section submittal label (Submittal Review Plan Phase 1, Decision D2).
 *
 * `sections` is the submittal's ordered `submittal_spec_sections` rows (or omitted / empty —
 * falls back to the submittal's own flat spec_section_number/name columns, i.e. today's single-
 * section shape). Format: "<numbers joined by ' + '> — <names joined by ' / '>", degrading
 * gracefully when numbers or names are missing on some/all sections, and finally to gc_number
 * or 'Submittal' when nothing at all is available.
 *
 * A single-section submittal renders byte-identical to the pre-Phase-1 inline computation:
 * `[s.spec_section_number, s.spec_section_name].filter(Boolean).join(' — ') || s.gc_number || 'Submittal'`.
 *
 * Keep in sync with the server mirror: api/_submittalLabel.js.
 */
export function submittalLabel(submittal, sections) {
  const s = submittal || {}
  let secs = sections && sections.length ? sections : (s.sections && s.sections.length ? s.sections : null)
  if (!secs) {
    secs = s.spec_section_number
      ? [{ section_number: s.spec_section_number, section_name: s.spec_section_name }]
      : []
  }
  const numbers = secs.map(sec => sec.section_number).filter(Boolean)
  const names = secs.map(sec => sec.section_name).filter(Boolean)
  const parts = []
  if (numbers.length) parts.push(numbers.join(' + '))
  if (names.length) parts.push(names.join(' / '))
  const joined = parts.join(' — ')
  return joined || s.gc_number || 'Submittal'
}

/** Days between received-from-GC and returned-to-GC (null if not both set). */
export function submittalTurnaround(s) {
  if (!s?.date_received || !s?.date_returned) return null
  const a = new Date(s.date_received), b = new Date(s.date_returned)
  if (isNaN(a) || isNaN(b)) return null
  return Math.round((b - a) / 86400000)
}

export function getContractSubmittals(contractId) {
  if (!contractId) return Promise.resolve([])
  return call('GET', null, 'contractId=' + encodeURIComponent(contractId)).then(d => d.submittals || [])
}
/** Submittals linked to a spec section (shown under the spec section in the outline). */
export function getSpecSectionSubmittals(specSectionId) {
  if (!specSectionId) return Promise.resolve([])
  return call('GET', null, 'specSectionId=' + encodeURIComponent(specSectionId)).then(d => d.submittals || [])
}
export function getSubmittal(submittalId) {
  return call('GET', null, 'submittalId=' + encodeURIComponent(submittalId)).then(d => d.submittal)
}
/** Reverse lookup: the submittal linked to a Cortex thread (thread → CA mount, legacy items only). */
export function getSubmittalByThread(threadAirtableId) {
  if (!threadAirtableId) return Promise.resolve(null)
  return call('GET', null, 'threadId=' + encodeURIComponent(threadAirtableId)).then(d => d.submittal || null)
}
/** Reverse lookup: the submittal linked to a milestone (milestone → CA mount). */
export function getSubmittalByMilestone(milestoneAirtableId) {
  if (!milestoneAirtableId) return Promise.resolve(null)
  return call('GET', null, 'milestoneId=' + encodeURIComponent(milestoneAirtableId)).then(d => d.submittal || null)
}
/**
 * `sections` (optional): ordered array of { spec_section_id?, section_number, section_name },
 * first entry = primary. When provided, replace-all writes submittal_spec_sections and mirrors
 * the primary (index 0) into the flat spec_section_number/name/id columns (Decision D1).
 */
export function createSubmittal(contractId, projectId, fields, sections) {
  return call('POST', { action: 'createSubmittal', contractId, projectId, fields, sections }).then(d => d.submittal)
}
export function updateSubmittal(submittalId, fields, sections) {
  return call('POST', { action: 'updateSubmittal', submittalId, fields, sections }).then(d => d.ok)
}
/** Clear the NEW highlight on a portal-submitted submittal once a team member views it. */
export function markSubmittalSeen(submittalId) {
  return call('POST', { action: 'updateSubmittal', submittalId, fields: { team_seen_at: new Date().toISOString() } }).then(d => d.ok)
}
export function deleteSubmittal(submittalId) {
  return call('POST', { action: 'deleteSubmittal', submittalId }).then(d => d.ok)
}

// ── Revision chain ────────────────────────────────────────────────────────────

/** List all revisions for a submittal, ordered by revision_number ascending. */
export function getSubmittalRevisions(submittalId) {
  if (!submittalId) return Promise.resolve([])
  return call('GET', null, 'action=revisions&submittalId=' + encodeURIComponent(submittalId))
    .then(d => d.revisions || [])
}

/**
 * Create a new revision on a submittal. Auto-increments revision_number and
 * links parent_revision_id. Also updates submittals.current_revision_id.
 * fields: { disposition, review_comments, date_received, date_sent,
 *           date_received_back, date_returned }
 */
export function createSubmittalRevision(submittalId, fields) {
  return call('POST', { action: 'createRevision', submittalId, fields }).then(d => d.revision)
}

/**
 * Update an existing revision (dates, disposition, comments, pdf url/path).
 */
export function updateSubmittalRevision(revisionId, fields) {
  return call('POST', { action: 'updateRevision', revisionId, fields }).then(d => d.ok)
}

// ── Sequential numbering ──────────────────────────────────────────────────────

/**
 * Compute the next firm-assigned submittal number for this project + spec section.
 * Returns a string like "06 10 00-001". Does not reserve/lock — caller writes it
 * on createSubmittal. Concurrent creation in the same section is unlikely but
 * safe: the unique index on (project_id, submittal_number) will reject the second.
 */
export function getNextSubmittalNumber(projectId, specSectionNumber) {
  if (!projectId || !specSectionNumber) return Promise.resolve(null)
  return call('POST', { action: 'nextSubmittalNumber', projectId, specSectionNumber })
    .then(d => d.number || null)
}
